import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import axios from 'axios'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
  confirmEmail: (email: string, confirmationCode: string) => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  confirmForgotPassword: (email: string, confirmationCode: string, newPassword: string) => Promise<void>
  refreshTokens: () => Promise<void>
}

interface User {
  id: string
  email: string
  name: string
  role: 'parent' | 'admin'
}

interface AuthTokens {
  accessToken: string
  idToken: string
  refreshToken: string
  expiresIn: number
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedTokens = localStorage.getItem('authTokens')
    const storedUser = localStorage.getItem('user')

    if (storedTokens && storedUser) {
      try {
        const parsedTokens = JSON.parse(storedTokens)
        const parsedUser = JSON.parse(storedUser)
        setTokens(parsedTokens)
        setUser(parsedUser)
        setIsAuthenticated(true)
        
        // Set default authorization header - use idToken for Cognito authorizer
        axios.defaults.headers.common['Authorization'] = `Bearer ${parsedTokens.idToken}`
      } catch (err) {
        console.error('Failed to restore auth state:', err)
        localStorage.removeItem('authTokens')
        localStorage.removeItem('user')
      }
    }

    setLoading(false)
  }, [])

  // Refresh token periodically
  useEffect(() => {
    if (!tokens) return

    const expirationTime = tokens.expiresIn * 1000 - 60000 // Refresh 1 minute before expiry
    const timer = setTimeout(() => {
      refreshTokens()
    }, expirationTime)

    return () => clearTimeout(timer)
  }, [tokens])

  const login = async (email: string, password: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      })

      const { accessToken, idToken, refreshToken, expiresIn } = response.data

      // Decode ID token to get user info
      const userInfo = decodeToken(idToken)
      const userData: User = {
        id: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name,
        role: userInfo['custom:role'] || 'parent'
      }

      const authTokens: AuthTokens = {
        accessToken,
        idToken,
        refreshToken,
        expiresIn
      }

      setTokens(authTokens)
      setUser(userData)
      setIsAuthenticated(true)

      // Store in localStorage
      localStorage.setItem('authTokens', JSON.stringify(authTokens))
      localStorage.setItem('user', JSON.stringify(userData))

      // Set default authorization header - use idToken for Cognito authorizer
      axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const signup = async (email: string, password: string, name: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/signup`, {
        email,
        password,
        name
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Signup failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const confirmEmail = async (email: string, confirmationCode: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/confirm`, {
        email,
        confirmationCode
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Email confirmation failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    setTokens(null)
    setUser(null)
    setIsAuthenticated(false)
    setError(null)

    localStorage.removeItem('authTokens')
    localStorage.removeItem('user')

    delete axios.defaults.headers.common['Authorization']
  }

  const changePassword = async (oldPassword: string, newPassword: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        oldPassword,
        newPassword
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Password change failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const forgotPassword = async (email: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, {
        email
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Forgot password request failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const confirmForgotPassword = async (email: string, confirmationCode: string, newPassword: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/confirm-forgot-password`, {
        email,
        confirmationCode,
        newPassword
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Password reset failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const refreshTokens = async () => {
    if (!tokens?.refreshToken) return

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
        refreshToken: tokens.refreshToken
      })

      const { accessToken, idToken, expiresIn } = response.data

      const newTokens: AuthTokens = {
        ...tokens,
        accessToken,
        idToken,
        expiresIn
      }

      setTokens(newTokens)
      localStorage.setItem('authTokens', JSON.stringify(newTokens))
      // Set default authorization header - use idToken for Cognito authorizer
      axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`
    } catch (err) {
      console.error('Token refresh failed:', err)
      logout()
    }
  }

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    signup,
    logout,
    confirmEmail,
    changePassword,
    forgotPassword,
    confirmForgotPassword,
    refreshTokens
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper function to decode JWT token
function decodeToken(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (err) {
    console.error('Failed to decode token:', err)
    return {}
  }
}
